import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Grid,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import useCoreProduct from "../../services/useCoreProduct";
import useDeleteCoreProduct from "../../services/useDeleteCoreProduct";
import { useState } from "react";

/**
 * Página de detalhes do produto do catálogo (core.Product)
 */
const CoreProductDetail = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const productId = idParam ? Number(idParam) : 0;
  const {
    data: product,
    isLoading,
    isError,
    error,
  } = useCoreProduct(productId);
  const deleteCoreProductMutation = useDeleteCoreProduct();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    setErrorMessage("");
  };

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
    deleteCoreProductMutation.mutate(productId, {
      onSuccess: () => {
        navigate("/app/core-products");
      },
      onError: (err: any) => {
        console.error("Erro ao excluir produto", err);
        const data = err?.response?.data ?? err;
        if (data && typeof data === "object") {
          const errorMessages = Object.keys(data).map((key) => {
            const value = (data as any)[key];
            const messages = Array.isArray(value) ? value : [value];
            return `${key}: ${messages.join(", ")}`;
          });
          setErrorMessage(errorMessages.join("\n"));
        } else {
          setErrorMessage(
            "Erro ao excluir produto. Verifique se o produto não possui ofertas vinculadas.",
          );
        }
      },
    });
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (isError || !product) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {error
            ? "Erro ao carregar produto. Tente novamente."
            : "Produto não encontrado."}
        </Alert>
        <Box sx={{ mt: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/app/core-products")}
          >
            Voltar
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h4" component="h1">
          Detalhes do produto (novo catálogo)
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/app/core-products")}
          >
            Voltar
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/app/core-products/${productId}/edit`)}
          >
            Editar
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
            disabled={deleteCoreProductMutation.isLoading}
          >
            Excluir
          </Button>
        </Box>
      </Box>

      {errorMessage && (
        <Alert
          severity="error"
          onClose={() => setErrorMessage("")}
          sx={{ mb: 3 }}
        >
          {errorMessage}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Grid container spacing={3}>
            {/* Informações principais */}
            <Grid item xs={12} md={8}>
              <Typography variant="h5" gutterBottom>
                {product.name}
              </Typography>

              <Box
                sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}
              >
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Produtor
                  </Typography>
                  <Typography variant="body1">
                    {product.producer?.name || "-"}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Categoria
                  </Typography>
                  <Typography variant="body1">
                    {product.category?.name || "-"}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Unidade
                  </Typography>
                  <Typography variant="body1">{product.unit || "-"}</Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Refrigerado
                  </Typography>
                  <Typography variant="body1">
                    {product.refrigerated ? "Sim" : "Não"}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={product.active ? "Ativo" : "Inativo"}
                    size="small"
                    color={product.active ? "success" : "default"}
                    sx={{ mt: 0.5 }}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Ordem de empilhamento
                  </Typography>
                  <Typography variant="body1">
                    {product.stack_order || "-"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Imagem */}
            {product.image && (
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                  }}
                >
                  <Avatar
                    src={product.image}
                    alt={product.name}
                    variant="rounded"
                    sx={{
                      width: "100%",
                      height: "auto",
                      maxWidth: 300,
                      aspectRatio: "1",
                    }}
                  />
                </Box>
              </Grid>
            )}

            {/* Descrição */}
            {product.description && (
              <Grid item xs={12}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Descrição
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {product.description}
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Ingredientes */}
            {product.ingredients && (
              <Grid item xs={12}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Ingredientes
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {product.ingredients}
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Badges */}
            {product.badges && product.badges.length > 0 && (
              <Grid item xs={12}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Badges
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {product.badges.map((badge) => (
                      <Chip
                        key={badge.id}
                        label={badge.name}
                        icon={
                          badge.icon ? (
                            <span style={{ fontSize: "16px" }}>
                              {badge.icon}
                            </span>
                          ) : undefined
                        }
                        style={{
                          backgroundColor: badge.color || undefined,
                          color: badge.color ? "#fff" : undefined,
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Datas */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Data de criação
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(product.created_at)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Última atualização
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(product.updated_at)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Dialog de confirmação de exclusão */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza de que deseja excluir este produto do catálogo?
          </DialogContentText>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Essa ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancelar</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteCoreProductMutation.isLoading}
          >
            {deleteCoreProductMutation.isLoading ? "Excluindo..." : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CoreProductDetail;
